// <MobileStartPage> — T-285, mobile-three-buttons-implementation-plan.md §4.
//
// The simple mobile homescreen: three evenly-tall buttons (Request Vacation,
// Report Sick, Approve pending requests). The third button is OWNER/ADMIN-only
// and navigates to /m/approvals where the manager's approvals inbox lives.
//
// All actions reuse production endpoints (the spec is frontend-only):
//   - GET   /api/employees/:id/timeoff/tab        (policies + my data)
//   - POST  /api/timeoff                          (create — via RequestModal)
//
// The <RequestModal> from the Profile time-off tab is mounted here so the
// field validation, soft-warning confirmation, and policy-resolves logic
// stay in one place.

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Palmtree,
  Thermometer,
  CheckCircle2,
} from 'lucide-react';
import type {
  TimeOffPolicy,
  TimeOffTabResponse,
} from '@pa/shared';
import { api } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { RequestModal } from '../profile/tabs/timeOff/RequestModal';

type ReportKind = 'vacation' | 'sick';

export default function MobileStartPage() {
  const navigate = useNavigate();
  const { me } = useAuth();
  const employeeId = me?.user.employeeId ?? null;
  const canApprove =
    me?.user.role === 'OWNER' || me?.user.role === 'ADMIN';

  const [reporting, setReporting] = useState<ReportKind | null>(null);

  // ── Fetch policies (also unlocks balances + my-requests if needed) ──
  const tab = useQuery({
    queryKey: ['timeoff-tab', employeeId],
    enabled: !!employeeId,
    queryFn: () =>
      api.get<TimeOffTabResponse>(
        `/api/employees/${employeeId}/timeoff/tab`,
      ),
    staleTime: 5 * 60_000,
  });

  const policies = tab.data?.policies ?? [];
  const vacationPolicy = useMemo(() => resolveVacation(policies), [policies]);
  const sickPolicy = useMemo(() => resolveSick(policies), [policies]);

  const reportingPolicyId =
    reporting === 'vacation'
      ? vacationPolicy?.id
      : reporting === 'sick'
        ? sickPolicy?.id
        : null;

  const vacationDisabled = !vacationPolicy && !tab.isLoading;
  const sickDisabled = !sickPolicy && !tab.isLoading;

  return (
    <div
      className="min-h-screen bg-[#F8F9FB] flex flex-col"
      data-testid="mobile-start-page"
    >
      <main className="flex-1 px-4 py-5 flex flex-col gap-4">
        {/* Vacation button */}
        {tab.isLoading ? (
          <div
            className="flex-1 bg-slate-100 animate-pulse rounded-3xl"
            data-testid="mobile-start-vacation-skeleton"
          />
        ) : (
          <button
            type="button"
            onClick={() => setReporting('vacation')}
            disabled={!vacationPolicy}
            data-testid="mobile-start-vacation-button"
            title={
              vacationDisabled
                ? 'No Vacation policy configured. Ask an admin to set one up under Settings → Time off.'
                : undefined
            }
            className={`flex-1 bg-white border rounded-3xl font-bold text-lg flex flex-col items-center justify-center gap-3 px-6 py-8 transition-all ${
              !vacationPolicy
                ? 'border-slate-200 opacity-50 cursor-not-allowed'
                : 'border-slate-200 hover:border-emerald-200/80 active:scale-[0.98]'
            }`}
          >
            <Palmtree
              size={32}
              className={!vacationPolicy ? 'text-slate-400' : 'text-emerald-500'}
            />
            Request Vacation
          </button>
        )}

        {/* Sick button */}
        {tab.isLoading ? (
          <div
            className="flex-1 bg-slate-100 animate-pulse rounded-3xl"
            data-testid="mobile-start-sick-skeleton"
          />
        ) : (
          <button
            type="button"
            onClick={() => setReporting('sick')}
            disabled={!sickPolicy}
            data-testid="mobile-start-sick-button"
            title={
              sickDisabled
                ? 'No Sick policy configured. Ask an admin to set one up under Settings → Time off.'
                : undefined
            }
            className={`flex-1 bg-white border rounded-3xl font-bold text-lg flex flex-col items-center justify-center gap-3 px-6 py-8 transition-all ${
              !sickPolicy
                ? 'border-slate-200 opacity-50 cursor-not-allowed'
                : 'border-slate-200 hover:border-rose-200/80 active:scale-[0.98]'
            }`}
          >
            <Thermometer
              size={32}
              className={!sickPolicy ? 'text-slate-400' : 'text-rose-500'}
            />
            Report Sick
          </button>
        )}

        {/* Approve button (OWNER/ADMIN only) */}
        {tab.isLoading ? (
          canApprove && (
            <div
              className="flex-1 bg-slate-100 animate-pulse rounded-3xl"
              data-testid="mobile-start-approve-skeleton"
            />
          )
        ) : (
          canApprove && (
            <button
              type="button"
              onClick={() => navigate('/m/approvals')}
              data-testid="mobile-start-approve-button"
              className="flex-1 bg-white border border-slate-200 rounded-3xl font-bold text-lg flex flex-col items-center justify-center gap-3 px-6 py-8 hover:border-[#6344C6]/30 active:scale-[0.98] transition-all"
            >
              <CheckCircle2 size={32} className="text-[#6344C6]" />
              Approve Sick + Vacation
            </button>
          )
        )}
      </main>

      {reporting && employeeId && reportingPolicyId && (
        <RequestModal
          employeeId={employeeId}
          policies={preselect(policies, reportingPolicyId)}
          onClose={() => {
            setReporting(null);
          }}
        />
      )}
    </div>
  );
}

// ── Policy resolution helpers (spec §4.2) ───────────────────────────

export function resolveVacation(policies: TimeOffPolicy[]): TimeOffPolicy | null {
  const exact = policies.find((p) => p.name === 'Vacation');
  if (exact) return exact;
  return (
    policies.find(
      (p) => p.balanceType === 'days' && p.name !== 'Sick',
    ) ?? null
  );
}

export function resolveSick(policies: TimeOffPolicy[]): TimeOffPolicy | null {
  const exact = policies.find((p) => p.name === 'Sick');
  if (exact) return exact;
  return policies.find((p) => p.balanceType === 'unlimited') ?? null;
}

// Reorder the policies list so the chosen policy is first — the
// RequestModal's initial state defaults to `policies[0]`.
function preselect(
  policies: TimeOffPolicy[],
  preferredId: string,
): TimeOffPolicy[] {
  const idx = policies.findIndex((p) => p.id === preferredId);
  if (idx <= 0) return policies;
  const next = [...policies];
  const [chosen] = next.splice(idx, 1);
  next.unshift(chosen);
  return next;
}
